import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Link2Off, ExternalLink } from "lucide-react"
import type { PreviewData } from "@/lib/types"

interface SubPreviewSelectionModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  compatiblePreviews: any[]
  preview: PreviewData
  editSubPreviewId: string | null 
  onSelect: (previewId: string | null) => void
  isLoading?: boolean
}

export function SubPreviewSelectionModal({
  isOpen,
  onOpenChange,
  compatiblePreviews,
  preview,
  editSubPreviewId,
  onSelect,
  isLoading = false,
}: SubPreviewSelectionModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(editSubPreviewId)
  
  // Update selected ID when the editSubPreviewId changes
  useEffect(() => {
    setSelectedId(editSubPreviewId);
  }, [editSubPreviewId]);

  // Update when modal opens to ensure latest state
  useEffect(() => {
    if (isOpen) {
      setSelectedId(editSubPreviewId);
    }
  }, [isOpen, editSubPreviewId]);

  const handleConfirm = () => {
    onSelect(selectedId)
    onOpenChange(false)
  }

  const openInNewTab = (previewId: string) => {
    window.open(`/viewer/${previewId}`, '_blank');
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Select a {compatiblePreviews.length > 0 && compatiblePreviews[0].model?.name ? 
              compatiblePreviews[0].model.name : 
              "Preview"}
          </DialogTitle>
          <DialogDescription>
            Choose a compatible preview to use as a sub-preview label
          </DialogDescription>
        </DialogHeader>
        
        <div className="max-h-[60vh] overflow-y-auto py-4">
          <RadioGroup value={selectedId || ""} onValueChange={value => setSelectedId(value || null)}>
            <div className="space-y-2">
              {compatiblePreviews.map((compatiblePreview) => (
                <div 
                  key={compatiblePreview.id} 
                  className="flex items-center space-x-2 rounded-md border p-2"
                >
                  <RadioGroupItem 
                    value={compatiblePreview.id} 
                    id={compatiblePreview.id} 
                  />
                  <Label 
                    htmlFor={compatiblePreview.id} 
                    className={`flex-grow cursor-pointer ${compatiblePreview.id === preview.id ? "text-muted-foreground" : ""}`}
                  >
                    {compatiblePreview.name}
                    {compatiblePreview.id === preview.id && " (current preview)"}
                  </Label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.preventDefault();
                      openInNewTab(compatiblePreview.id);
                    }}
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center space-x-2 rounded-md border p-2 mt-4">
                <RadioGroupItem value="" id="none" />
                <Label htmlFor="none" className="flex-grow cursor-pointer flex items-center">
                  <Link2Off className="w-4 h-4 mr-2" /> No sub-preview
                </Label>
              </div>
            </div>
          </RadioGroup>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? "Saving..." : "Confirm Selection"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
